import React, { useState, useEffect, useCallback } from 'react';
import {
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineLoading,
  AiOutlinePicture,
  AiOutlineClose,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineSearch,
  AiOutlineSwap,
  AiOutlineArrowUp,
  AiOutlineArrowDown,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineSave,
  AiOutlineCloudUpload
} from 'react-icons/ai';
import { getData, postData, serverURL } from '../../../../services/Fetchnodeservices';

const RestaurantLoginBanner = ({ isDark = false }) => {
  // ── State ──
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');

  // ── Form State for 3 Images ──
  const [formData, setFormData] = useState({
    images: [null, null, null],
    existingImages: ['', '', ''],
    imagePreviews: [null, null, null],
  });

  // ── Theme ──
  const theme = {
    bg: isDark ? '#0f172a' : '#f8fafc',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    textMuted: isDark ? '#64748b' : '#94a3b8',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
    hoverBg: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
    primary: '#8b5cf6',
    primaryLight: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
    success: '#10b981',
    successLight: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)',
    danger: '#ef4444',
    dangerLight: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  };

  const API_BASE = 'RestaurantOfferBanne';

  // ── Fetch Banners (GET) ──
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getData(`${API_BASE}/restaurant-login-bannerfetch`);
      let bannerData = [];
      
      if (result.success === true && result.data) bannerData = result.data;
      else if (Array.isArray(result)) bannerData = result;
      else if (result.data && Array.isArray(result.data)) bannerData = result.data;
      
      setBanners(bannerData);
    } catch (error) {
      console.error('❌ [fetchBanners] Error:', error);
      alert('Error fetching banners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // ── Handle Image Upload ──
  const handleImageUpload = (index, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImages = [...formData.images];
        const newPreviews = [...formData.imagePreviews];
        newImages[index] = file;
        newPreviews[index] = reader.result;
        setFormData(prev => ({ ...prev, images: newImages, imagePreviews: newPreviews }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    const newPreviews = [...formData.imagePreviews];
    const newExisting = [...formData.existingImages];
    newImages[index] = null;
    newPreviews[index] = null;
    newExisting[index] = '';
    setFormData(prev => ({ ...prev, images: newImages, imagePreviews: newPreviews, existingImages: newExisting }));
  };

  const resetForm = () => {
    setFormData({ images: [null, null, null], existingImages: ['', '', ''], imagePreviews: [null, null, null] });
  };

  const openCreateModal = () => { resetForm(); setShowModal(true); };
  const closeModal = () => { setShowModal(false); resetForm(); };

  // ── Create Banner (POST) ──
  const createBanner = async () => {
    const hasImage = formData.images.some(img => img !== null);
    if (!hasImage) { alert('Please select at least one image'); return; }

    setSubmitting(true);
    try {
      const formDataObj = new FormData();
      formData.images.filter(img => img !== null).forEach((img) => {
        formDataObj.append('images', img);
      });

      const result = await postData(`${API_BASE}/restaurant-login-banner`, formDataObj);
      if (result.success === true) {
        alert('✅ Login banner added successfully!');
        closeModal();
        fetchBanners();
      } else {
        alert('❌ Failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ [createBanner] Error:', error);
      alert('❌ Error creating banner');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete Banner ──
  const deleteBanner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this login banner?')) return;
    try {
      const result = await postData(`${API_BASE}/restaurant-login-banner/${id}`);
      if (result.success === true) {
        alert('✅ Deleted successfully!');
        fetchBanners();
      } else {
        alert('❌ Failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      alert('❌ Error deleting banner');
    }
  };

  // ── Toggle Status ──
  const toggleStatus = async (id) => {
    try {
      const result = await postData(`${API_BASE}/restaurant-login-banner/status/${id}`, {});
      if (result.success === true) {
        fetchBanners();
      } else {
        alert('❌ Failed to update status');
      }
    } catch (error) {
      alert('❌ Error updating status');
    }
  };

  // ── Filter and Sort ──
  const getFilteredBanners = () => {
    let filtered = [...banners];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(banner =>
        banner.id.toString().includes(query) ||
        (banner.image1 && banner.image1.toLowerCase().includes(query)) ||
        (banner.image2 && banner.image2.toLowerCase().includes(query)) ||
        (banner.image3 && banner.image3.toLowerCase().includes(query))
      );
    }
    filtered.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredBanners();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return {
      data: filtered.slice(startIndex, startIndex + itemsPerPage),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage) || 1
    };
  };

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <AiOutlineSwap style={{ fontSize: '12px', opacity: 0.3 }} />;
    return sortOrder === 'asc' ? 
      <AiOutlineArrowUp style={{ fontSize: '12px', color: theme.primary }} /> : 
      <AiOutlineArrowDown style={{ fontSize: '12px', color: theme.primary }} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    if (imageName.startsWith('http://') || imageName.startsWith('https://')) return imageName;
    const filename = imageName.includes('/') ? imageName.split('/').pop() : imageName;
    return `${serverURL}/uploadnew/upload/${filename}`;
  };

  // ── Image Upload Component ──
  const ImageUploadField = ({ index, label }) => {
    const preview = formData.imagePreviews[index];
    const existing = formData.existingImages[index];
    const hasImage = preview || existing;

    return (
      <div style={styles.uploadWrapper}>
        <label style={styles.uploadLabel}>{label}</label>
        <div
          className="upload-zone"
          onClick={() => document.getElementById(`imageInput${index}`).click()}
        >
          {hasImage ? (
            <div style={styles.previewContainer}>
              <img src={preview || getImageUrl(existing)} alt={label} style={styles.imagePreview} />
              <button className="remove-btn" onClick={(e) => { e.stopPropagation(); removeImage(index); }}>
                <AiOutlineClose size={14} />
              </button>
            </div>
          ) : (
            <div style={styles.emptyUpload}>
              <AiOutlineCloudUpload style={styles.uploadIcon} />
              <span style={styles.uploadText}>Click to upload</span>
            </div>
          )}
          <input id={`imageInput${index}`} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => e.target.files[0] && handleImageUpload(index, e.target.files[0])} />
        </div>
      </div>
    );
  };

  // ── Styles ──
  const styles = {
    container: { backgroundColor: theme.bg, minHeight: '100vh', padding: '32px 24px', fontFamily: "'Inter', -apple-system, sans-serif", color: theme.text },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' },
    headerTitle: { fontSize: '24px', fontWeight: '800', color: theme.text, display: 'flex', alignItems: 'center', gap: '12px', letterSpacing: '-0.5px' },
    badge: { display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: theme.primaryLight, color: theme.primary },
    btnPrimary: { padding: '12px 24px', borderRadius: '12px', border: 'none', background: theme.gradient, color: '#fff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' },
    searchWrapper: { position: 'relative', maxWidth: '400px', width: '100%', marginBottom: '24px' },
    searchInput: { width: '100%', padding: '12px 16px 12px 44px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, backgroundColor: theme.cardBg, color: theme.text, fontSize: '14px', outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box' },
    searchIcon: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted, fontSize: '16px' },
    card: { background: theme.cardBg, borderRadius: '16px', border: `1px solid ${theme.border}`, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '16px 20px', backgroundColor: theme.hoverBg, color: theme.textSecondary, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', borderBottom: `1px solid ${theme.border}` },
    td: { padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, color: theme.text, fontSize: '14px', verticalAlign: 'middle' },
    imageCell: { display: 'flex', gap: '8px', alignItems: 'center' },
    miniThumb: { width: '48px', height: '48px', borderRadius: '10px', objectFit: 'cover', border: `1px solid ${theme.border}`, backgroundColor: theme.hoverBg },
    statusBadge: (active) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', backgroundColor: active ? theme.successLight : theme.dangerLight, color: active ? theme.success : theme.danger }),
    actionBtn: (color) => ({ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '8px', transition: 'all 0.2s ease', color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: `1px solid ${theme.border}`, flexWrap: 'wrap', gap: '16px' },
    paginationInfo: { color: theme.textSecondary, fontSize: '13px', fontWeight: '500' },
    pageBtn: (disabled) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: `1px solid ${disabled ? theme.inputBorder : theme.inputBorder}`, background: disabled ? theme.hoverBg : theme.cardBg, color: disabled ? theme.textMuted : theme.text, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }),
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
    modalContent: { background: theme.cardBg, borderRadius: '20px', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '32px', border: `1px solid ${theme.border}`, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', position: 'relative', animation: 'slideUp 0.3s ease-out' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${theme.border}` },
    modalTitle: { fontSize: '20px', fontWeight: '700', color: theme.text, margin: 0 },
    closeBtn: { background: theme.hoverBg, border: 'none', width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer', color: theme.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' },
    imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginTop: '8px' },
    uploadWrapper: { display: 'flex', flexDirection: 'column', gap: '8px' },
    uploadLabel: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    previewContainer: { position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden', border: `1px solid ${theme.border}` },
    imagePreview: { width: '100%', height: '100%', objectFit: 'cover' },
    uploadWrapper: { display: 'flex', flexDirection: 'column', gap: '8px' },
    uploadLabel: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    emptyUpload: { width: '100%', aspectRatio: '16/9', borderRadius: '12px', border: `2px dashed ${theme.inputBorder}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: theme.hoverBg, transition: 'all 0.2s ease' },
    uploadIcon: { fontSize: '28px', color: theme.textMuted },
    uploadText: { fontSize: '12px', color: theme.textSecondary, fontWeight: '500' },
    modalActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px', paddingTop: '20px', borderTop: `1px solid ${theme.border}` },
    btnSecondary: { padding: '12px 24px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: 'transparent', color: theme.text, fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' },
    emptyState: { textAlign: 'center', padding: '60px 20px', color: theme.textSecondary },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px' },
    spinner: { fontSize: '40px', color: theme.primary, animation: 'spin 1s linear infinite' },
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <AiOutlineLoading style={styles.spinner} />
        <p style={{ color: theme.textSecondary, fontWeight: '500' }}>Loading banners...</p>
      </div>
    );
  }

  const { data: paginatedData, total, totalPages } = getPaginatedData();

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        
        .upload-zone:hover { border-color: ${theme.primary} !important; background-color: ${theme.primaryLight} !important; }
        .upload-zone:hover .upload-icon { color: ${theme.primary} !important; }
        .remove-btn { position: absolute; top: '8px'; right: '8px'; background: rgba(239, 68, 68, 0.9); color: white; border: none; width: 28px; height: 28px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; backdrop-filter: blur(4px); }
        .remove-btn:hover { background: #dc2626; transform: scale(1.1); }
        
        .action-toggle:hover { background-color: ${theme.successLight} !important; color: ${theme.success} !important; }
        .action-delete:hover { background-color: ${theme.dangerLight} !important; color: ${theme.danger} !important; }
        .close-btn:hover { background-color: ${theme.dangerLight} !important; color: ${theme.danger} !important; }
        .page-btn-active { background-color: ${theme.primary} !important; color: white !important; border-color: ${theme.primary} !important; }
        .page-btn:hover:not(:disabled) { background-color: ${theme.hoverBg} !important; transform: translateY(-1px); }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4) !important; }
        .btn-secondary:hover { background-color: ${theme.hoverBg} !important; }
        tr.table-row { transition: background-color 0.2s ease; animation: fadeIn 0.4s ease-out; }
        tr.table-row:hover { background-color: ${theme.hoverBg} !important; }
        input:focus { border-color: ${theme.primary} !important; box-shadow: 0 0 0 3px ${theme.primaryLight} !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <AiOutlinePicture style={{ color: theme.primary, fontSize: '28px' }} />
          Login Banners
          <span style={styles.badge}>{banners.length} Total</span>
        </div>
        <button className="btn-primary" style={styles.btnPrimary} onClick={openCreateModal}>
          <AiOutlinePlus size={18} /> Add New Banner
        </button>
      </div>

      {/* ── Search ── */}
      <div style={styles.searchWrapper}>
        <AiOutlineSearch style={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search by ID or image name..."
          style={styles.searchInput}
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
      </div>

      {/* ── Table ── */}
      <div style={styles.card}>
        {paginatedData.length === 0 ? (
          <div style={styles.emptyState}>
            <AiOutlinePicture size={64} style={{ color: theme.textMuted, marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px 0', color: theme.text }}>No banners found</h3>
            <p style={{ margin: 0, fontSize: '14px' }}>{searchQuery ? 'Try adjusting your search terms.' : 'Get started by adding your first login banner.'}</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th} onClick={() => handleSort('id')}>ID {getSortIcon('id')}</th>
                    <th style={styles.th}>Preview</th>
                    <th style={styles.th} onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
                    <th style={styles.th} onClick={() => handleSort('created_at')}>Created {getSortIcon('created_at')}</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((banner) => {
                    const images = [banner.image1, banner.image2, banner.image3].filter(img => img).map(getImageUrl);
                    return (
                      <tr key={banner.id} className="table-row">
                        <td style={styles.td}><span style={{ fontWeight: '700', color: theme.primary }}>#{banner.id}</span></td>
                        <td style={styles.td}>
                          <div style={styles.imageCell}>
                            {images.length > 0 ? images.slice(0, 3).map((url, idx) => (
                              <img key={idx} src={url} alt={`Img ${idx+1}`} style={styles.miniThumb} onError={(e) => { e.target.style.display = 'none'; }} />
                            )) : <span style={{ fontSize: '12px', color: theme.textMuted }}>No images</span>}
                            {images.length > 3 && <span style={{ fontSize: '11px', fontWeight: '600', color: theme.textSecondary }}>+{images.length - 3}</span>}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.statusBadge(banner.status === 1)}>
                            {banner.status === 1 ? '● Active' : '○ Inactive'}
                          </span>
                        </td>
                        <td style={{ ...styles.td, color: theme.textSecondary, fontSize: '13px' }}>{formatDate(banner.created_at)}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                            <button className="action-toggle" style={styles.actionBtn(banner.status === 1 ? theme.success : theme.textMuted)} onClick={() => toggleStatus(banner.id)} title={banner.status === 1 ? 'Deactivate' : 'Activate'}>
                              {banner.status === 1 ? <AiOutlineCheckCircle size={20} /> : <AiOutlineCloseCircle size={20} />}
                            </button>
                            <button className="action-delete" style={styles.actionBtn(theme.danger)} onClick={() => deleteBanner(banner.id)} title="Delete">
                              <AiOutlineDelete size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, total)} of {total}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="page-btn" style={styles.pageBtn(currentPage === 1)} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                  <AiOutlineLeft size={16} />
                </button>
                <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text, minWidth: '80px', textAlign: 'center' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button className="page-btn" style={styles.pageBtn(currentPage === totalPages)} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
                  <AiOutlineRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add New Login Banner</h3>
              <button className="close-btn" style={styles.closeBtn} onClick={closeModal}>
                <AiOutlineClose size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: theme.text, marginBottom: '12px', display: 'block' }}>
                Banner Images <span style={{ color: theme.textMuted, fontWeight: '400', fontSize: '13px' }}>(At least 1 required, max 3)</span>
              </label>
              <div style={styles.imageGrid}>
                <ImageUploadField index={0} label="Primary Image" />
                <ImageUploadField index={1} label="Secondary Image" />
                <ImageUploadField index={2} label="Tertiary Image" />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button className="btn-secondary" style={styles.btnSecondary} onClick={closeModal}>Cancel</button>
              <button className="btn-primary" style={{ ...styles.btnPrimary, opacity: submitting ? 0.7 : 1 }} onClick={createBanner} disabled={submitting}>
                {submitting ? <AiOutlineLoading style={{ animation: 'spin 1s linear infinite' }} /> : <AiOutlineSave size={18} />}
                {submitting ? 'Saving...' : 'Save Banner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantLoginBanner;